import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
// Limitless imports
import { useCustomerMessageContext, useCustomerSuggestedAnswers, useCustomerContext } from "@limitless/customer/hooks";
import { MESSAGE_STATE_REPORTED } from "@limitless/customer/utils";
import { ClientContext } from "@limitless/common/utils";
import { Header, Conversation, ConversationFooter, withLogging } from "@limitless/customer/components";
import Toast from "@limitless/customer/components/toast/Toast";
import { useAppVisibilityContext } from "../hooks/app-visibility/AppVisibilityContext";

const ConversationScreen = ({ logger }) => {
    const {
        customerSAState,
        selectSuggestedAnswer,
        updateDialogueVisiblity,
        submitSuggestedAnswer,
        rejectSuggestedAnswer
    } = useCustomerSuggestedAnswers();
    const { clearSuggestedAnswers, dialoguesVisibility } = customerSAState;

    const { message, fetchMessage } = useCustomerMessageContext();
    const { clearQuestion, isReturningCustomer } = useCustomerContext();
    const { toggleAppOpen, toggleInputFocus } = useAppVisibilityContext();
    const conversationComponent = React.useRef(null);
    const params = useParams();
    const history = useHistory();

    const isReturning = isReturningCustomer();

    React.useEffect(() => {
        logger.info("ConversationScreen loaded");
        const { messageId, accessKey, overrideCustConfirmation, overrideAccepted } = params;
        logger.info(`Fetching ${messageId} with key ${accessKey} & ${overrideCustConfirmation}`);
        if (messageId) {
            fetchMessage(messageId, accessKey, overrideCustConfirmation, overrideAccepted);

            window.LLM.link = function () {
                window.location = `${process.env.REACT_APP_PUBLIC_URL}?accessKey=${accessKey}&messageId=${messageId}`;
            };
        }
        clearQuestion();
    }, []);

    const navigateNewMessage = React.useMemo(() => {
        if (ClientContext._newConversationMode !== "standard") {
            //if the group is not configured to have submitMsgUrl don't allow new conversation
            if (!message.submitMsgUrl) {
                return null;
            }

            if (ClientContext._newConversationMode === "group-new-tab") {
                return () => window.open(message.submitMsgUrl, "_blank");
            }

            return () => window.open(message.submitMsgUrl, "_parent");
        }

        if (isReturning) {
            return () => history.push("/newMessage");
        }

        return () => history.push("/createMessage");
    }, [isReturning, message]);

    const scrollConversationCallback = () => {
        conversationComponent?.current?.forceUpdate();
    };

    const { typing, claimer, dialogues } = message;

    const { isHistoryMessage } = params;

    return (
        <div className="llm-view-container">
            <Header
                title="Your Conversation"
                onLeftIconClickHandle={() => {
                    history.push("/history");
                }}
                onCloseHandle={() => toggleAppOpen()}
            />
            <Toast />
            <Conversation
                typing={typing || message.claimedAt ? true : false}
                dialogues={dialogues}
                isHistoryMessage={isHistoryMessage === "true"}
                claimer={claimer}
                ref={conversationComponent}
                handleSuggestedAnswerSelection={selectSuggestedAnswer}
                clearSuggestedAnswers={clearSuggestedAnswers}
                dialoguesVisible={updateDialogueVisiblity}
                dialoguesVisibility={dialoguesVisibility}
                isReported={message.footerState === MESSAGE_STATE_REPORTED ? true : false}
                poweredByEnabled={ClientContext._poweredBy}
                message={message}
            />
            <ConversationFooter
                scrollConversationCallback={scrollConversationCallback}
                message={message}
                suggestedAnswerHandle={submitSuggestedAnswer}
                rejectSuggestedAnswerHandle={rejectSuggestedAnswer}
                newMessageHandle={navigateNewMessage}
                testimonialFullScreen={false}
                toggleInputFocus={toggleInputFocus}
            />
        </div>
    );
};

export default withLogging(ConversationScreen, "Conversation Screen");

ConversationScreen.propTypes = {
    logger: PropTypes.object.isRequired
};
